/**
 * Audit Log Page - Admin
 * HIPAA-compliant audit trail for all system activities
 */

import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api from '../../api';
import {
  Shield, Search, RefreshCw, Download, Filter, Calendar,
  User, FileText, Eye, Edit, Trash2, LogIn, LogOut, AlertTriangle,
  CheckCircle, Clock, Database, Lock, ChevronLeft, ChevronRight,
  Activity, Mail
} from 'lucide-react';

const AuditLogPage = () => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    thisWeek: 0,
    uniqueUsers: 0
  });

  const actionTypes = [
    { value: 'login', label: 'Login', icon: LogIn, color: 'green' },
    { value: 'LOGIN_SUCCESS', label: 'Login Success', icon: LogIn, color: 'green' },
    { value: 'LOGIN_FAILED', label: 'Login Failed', icon: LogIn, color: 'red' },
    { value: 'logout', label: 'Logout', icon: LogOut, color: 'gray' },
    { value: 'LOGOUT', label: 'Logout', icon: LogOut, color: 'gray' },
    { value: 'view', label: 'View Record', icon: Eye, color: 'blue' },
    { value: 'PHI_ACCESS', label: 'PHI Access', icon: Eye, color: 'blue' },
    { value: 'create', label: 'Create', icon: FileText, color: 'purple' },
    { value: 'DATA_CREATED', label: 'Data Created', icon: FileText, color: 'purple' },
    { value: 'USER_CREATE', label: 'User Created', icon: FileText, color: 'purple' },
    { value: 'update', label: 'Update', icon: Edit, color: 'orange' },
    { value: 'DATA_MODIFIED', label: 'Data Modified', icon: Edit, color: 'orange' },
    { value: 'delete', label: 'Delete', icon: Trash2, color: 'red' },
    { value: 'DATA_DELETED', label: 'Data Deleted', icon: Trash2, color: 'red' },
    { value: 'export', label: 'Export Data', icon: Download, color: 'teal' },
    { value: 'DATA_EXPORTED', label: 'Data Exported', icon: Download, color: 'teal' },
    { value: 'hipaa_ack', label: 'HIPAA Acknowledgment', icon: Shield, color: 'indigo' },
    { value: 'HIPAA_ACKNOWLEDGMENT', label: 'HIPAA Acknowledged', icon: Shield, color: 'indigo' },
    { value: 'access_denied', label: 'Access Denied', icon: Lock, color: 'red' },
    { value: 'ACCESS_DENIED', label: 'Access Denied', icon: Lock, color: 'red' },
    { value: 'NOTIFICATION_SENT', label: 'Notification Sent', icon: Mail, color: 'blue' },
    { value: 'ACCOUNT_LOCKED', label: 'Account Locked', icon: Lock, color: 'red' },
    { value: 'ACCOUNT_UNLOCKED', label: 'Account Unlocked', icon: CheckCircle, color: 'green' }
  ];

  useEffect(() => {
    fetchLogs();
  }, [currentPage, filterAction, filterUser, filterDateFrom, filterDateTo]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 25,
        action: filterAction !== 'all' ? filterAction : undefined,
        user_id: filterUser !== 'all' ? filterUser : undefined,
        date_from: filterDateFrom || undefined,
        date_to: filterDateTo || undefined
      };
      
      const response = await api.get('/admin/audit-logs', { params });
      setLogs(response.data?.logs || []);
      setTotalPages(response.data?.totalPages || 1);
      setStats(response.data?.stats || {
        total: 0,
        today: 0,
        thisWeek: 0,
        uniqueUsers: 0
      });
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Failed to load audit logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getMockLogs = () => [
    {
      id: '1',
      timestamp: new Date().toISOString(),
      userId: 'user1',
      userName: 'Sarah Johnson',
      userRole: 'Nurse',
      action: 'login',
      resource: 'Authentication',
      resourceId: null,
      details: 'Successful login from 192.168.1.100',
      ipAddress: '192.168.1.100',
      userAgent: 'Chrome/120.0.0.0'
    },
    {
      id: '2',
      timestamp: new Date(Date.now() - 300000).toISOString(),
      userId: 'user1',
      userName: 'Sarah Johnson',
      userRole: 'Nurse',
      action: 'hipaa_ack',
      resource: 'HIPAA Compliance',
      resourceId: null,
      details: 'Acknowledged HIPAA compliance terms',
      ipAddress: '192.168.1.100',
      userAgent: 'Chrome/120.0.0.0'
    },
    {
      id: '3',
      timestamp: new Date(Date.now() - 600000).toISOString(),
      userId: 'user1',
      userName: 'Sarah Johnson',
      userRole: 'Nurse',
      action: 'view',
      resource: 'Patient',
      resourceId: 'PAT-2024-001',
      details: 'Viewed patient demographics for John Smith',
      ipAddress: '192.168.1.100',
      userAgent: 'Chrome/120.0.0.0'
    },
    {
      id: '4',
      timestamp: new Date(Date.now() - 900000).toISOString(),
      userId: 'user2',
      userName: 'Michael Chen',
      userRole: 'Admin',
      action: 'update',
      resource: 'Patient',
      resourceId: 'PAT-2024-002',
      details: 'Updated insurance information for Jane Doe',
      ipAddress: '192.168.1.101',
      userAgent: 'Firefox/121.0'
    },
    {
      id: '5',
      timestamp: new Date(Date.now() - 1200000).toISOString(),
      userId: 'user3',
      userName: 'Emily Rodriguez',
      userRole: 'Biller',
      action: 'export',
      resource: 'Reports',
      resourceId: 'RPT-2024-001',
      details: 'Exported billing report for December 2024',
      ipAddress: '192.168.1.102',
      userAgent: 'Safari/17.2'
    },
    {
      id: '6',
      timestamp: new Date(Date.now() - 1800000).toISOString(),
      userId: 'user4',
      userName: 'Unknown User',
      userRole: 'N/A',
      action: 'access_denied',
      resource: 'Admin Panel',
      resourceId: null,
      details: 'Unauthorized access attempt to admin settings',
      ipAddress: '10.0.0.55',
      userAgent: 'Unknown'
    },
    {
      id: '7',
      timestamp: new Date(Date.now() - 3600000).toISOString(),
      userId: 'user2',
      userName: 'Michael Chen',
      userRole: 'Admin',
      action: 'create',
      resource: 'User',
      resourceId: 'USR-2024-010',
      details: 'Created new user account for David Wilson',
      ipAddress: '192.168.1.101',
      userAgent: 'Firefox/121.0'
    },
    {
      id: '8',
      timestamp: new Date(Date.now() - 7200000).toISOString(),
      userId: 'user1',
      userName: 'Sarah Johnson',
      userRole: 'Nurse',
      action: 'logout',
      resource: 'Authentication',
      resourceId: null,
      details: 'User logged out',
      ipAddress: '192.168.1.100',
      userAgent: 'Chrome/120.0.0.0'
    }
  ];

  const calculateStats = (logs) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    return {
      total: logs.length,
      today: logs.filter(l => new Date(l.timestamp) >= today).length,
      thisWeek: logs.filter(l => new Date(l.timestamp) >= weekAgo).length,
      uniqueUsers: new Set(logs.map(l => l.userId)).size
    };
  };

  const getActionIcon = (action) => {
    const actionType = actionTypes.find(a => a.value === action);
    const Icon = actionType?.icon || Activity;
    return <Icon className="w-4 h-4" />;
  };

  const getActionBadge = (action) => {
    const actionType = actionTypes.find(a => a.value === action);
    const colorClasses = {
      green: 'bg-green-100 text-green-700',
      gray: 'bg-gray-100 text-gray-700',
      blue: 'bg-blue-100 text-blue-700',
      purple: 'bg-purple-100 text-purple-700',
      orange: 'bg-orange-100 text-orange-700',
      red: 'bg-red-100 text-red-700',
      teal: 'bg-teal-100 text-teal-700',
      indigo: 'bg-indigo-100 text-indigo-700'
    };
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${colorClasses[actionType?.color] || colorClasses.gray}`}>
        {getActionIcon(action)}
        {actionType?.label || action}
      </span>
    );
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const filteredLogs = logs.filter(log => {
    // Handle details being either a string or an object
    const detailsString = typeof log.details === 'string' 
      ? log.details 
      : JSON.stringify(log.details || '');
    
    const matchesSearch = 
      log.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.userEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      detailsString.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.resource?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.eventType?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.ipAddress?.includes(searchQuery);
    
    return matchesSearch;
  });

  const handleExport = () => {
    // Create CSV content
    const headers = ['Timestamp', 'User', 'Role', 'Action', 'Resource', 'Details', 'IP Address'];
    const rows = filteredLogs.map(log => [
      formatTimestamp(log.timestamp),
      log.userName,
      log.userRole,
      log.action,
      log.resource,
      log.details,
      log.ipAddress
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Audit log exported successfully');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="audit-log-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-indigo-600" />
              Audit Log
            </h1>
            <p className="text-gray-600 mt-1">HIPAA-compliant activity tracking and compliance monitoring</p>
          </div>
          <Button 
            onClick={handleExport}
            variant="outline"
            className="gap-2"
            data-testid="export-audit-btn"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Database className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Events</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Today</p>
                <p className="text-2xl font-bold text-green-600">{stats.today}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">This Week</p>
                <p className="text-2xl font-bold text-blue-600">{stats.thisWeek}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Users</p>
                <p className="text-2xl font-bold text-purple-600">{stats.uniqueUsers}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by user, details, or IP address..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                  data-testid="audit-search"
                />
              </div>
            </div>
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Actions</option>
              {actionTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="From"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="To"
              />
            </div>
            <Button variant="ghost" onClick={fetchLogs} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Audit Log Table */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full" data-testid="audit-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Action</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Resource</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Details</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">IP Address</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-indigo-500" />
                    <p className="mt-2">Loading audit logs...</p>
                  </td>
                </tr>
              ) : filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No audit logs found for the selected filters
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => {
                  // Format details for display
                  const detailsDisplay = typeof log.details === 'string' 
                    ? log.details 
                    : log.details?.action || log.details?.reason || JSON.stringify(log.details || {}).substring(0, 50);
                  
                  return (
                  <tr key={log.id || log._id} className={`hover:bg-gray-50 ${log.eventType === 'ACCESS_DENIED' || log.action === 'access_denied' ? 'bg-red-50' : ''}`}>
                    <td className="px-4 py-3 text-gray-600 text-sm whitespace-nowrap">
                      {formatTimestamp(log.timestamp)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{log.userName || log.userEmail || 'System'}</p>
                          <p className="text-xs text-gray-500">{log.userRole || 'User'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getActionBadge(log.action || log.eventType)}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm text-gray-900">{log.resource || log.eventType || '-'}</p>
                        {log.resourceId && (
                          <p className="text-xs text-gray-500 font-mono">{log.resourceId}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={detailsDisplay}>
                      {detailsDisplay}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">
                      {log.ipAddress || '-'}
                    </td>
                  </tr>
                )})
              )}
            </tbody>
          </table>
          
          {/* Pagination */}
          <div className="px-4 py-3 border-t flex items-center justify-between bg-gray-50">
            <span className="text-sm text-gray-600">
              Showing {filteredLogs.length} of {stats.total} entries
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-3 py-1 text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* HIPAA Compliance Notice */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-indigo-800">HIPAA Compliance Notice</p>
              <p className="text-sm text-indigo-700 mt-1">
                This audit log maintains a complete record of all system access and PHI-related activities 
                as required by HIPAA regulations (45 CFR § 164.312(b)). Audit logs are retained for a 
                minimum of 6 years and are available for compliance audits upon request.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AuditLogPage;
